import { Timestamp } from 'firebase/firestore';

export type ReportType = 'event' | 'user';

export type ReportStatus = 'pending' | 'under_review' | 'resolved' | 'dismissed';

export type ReportPriority = 'low' | 'medium' | 'high' | 'urgent';

export enum ReportCategory {
  // Content Issues
  SPAM = 'spam',
  INAPPROPRIATE_CONTENT = 'inappropriate_content',
  MISLEADING_INFO = 'misleading_information',
  COPYRIGHT = 'copyright_violation',
  
  // Safety Issues  
  HARASSMENT = 'harassment',
  HATE_SPEECH = 'hate_speech',
  VIOLENCE = 'violence_threats',
  DOXXING = 'personal_info_sharing',
  
  // Event-Specific
  FAKE_EVENT = 'fake_event',
  DANGEROUS_EVENT = 'dangerous_event',
  PRICE_FRAUD = 'price_fraud',
  LOCATION_FRAUD = 'location_fraud',
  
  // User-Specific
  FAKE_PROFILE = 'fake_profile',
  IMPERSONATION = 'impersonation',
  SCAMMER = 'scammer',
  
  // Other
  OTHER = 'other'
}

export interface Report {
  id: string;
  type: ReportType;
  targetId: string; // eventId or userId
  reporterId: string;
  category: ReportCategory;
  reason: string;
  description?: string;
  evidence?: string[]; // image URLs
  status: ReportStatus;
  priority: ReportPriority;
  assignedTo?: string; // moderator ID
  createdAt: Timestamp;
  updatedAt: Timestamp;
  resolvedAt?: Timestamp;
  resolution?: string;
  moderatorNotes?: string;
  
  // Additional context
  targetTitle?: string; // event title or user display name
  targetType?: string; // for better context
  reporterName?: string; // cached for display
}

export interface CreateReportRequest {
  type: ReportType;
  targetId: string;
  category: ReportCategory;
  reason: string;
  description?: string;
  evidence?: File[];
}

export interface ReportAction {
  id: string;
  reportId: string;
  actionType: 'warning' | 'suspension' | 'ban' | 'content_removal' | 'no_action';
  targetId: string;
  moderatorId: string;
  duration?: number; // for suspensions (in days)
  reason: string;
  createdAt: Timestamp;
}

// Category descriptions for UI
export const REPORT_CATEGORY_INFO = {
  [ReportCategory.SPAM]: {
    title: 'Spam',
    description: 'Repetitive, unwanted, or irrelevant content'
  },
  [ReportCategory.INAPPROPRIATE_CONTENT]: {
    title: 'Inappropriate Content',
    description: 'Content that violates community guidelines'
  },
  [ReportCategory.MISLEADING_INFO]: {
    title: 'Misleading Information',
    description: 'False or misleading information about events or users'
  },
  [ReportCategory.COPYRIGHT]: {
    title: 'Copyright Violation',
    description: 'Unauthorized use of copyrighted material'
  },
  [ReportCategory.HARASSMENT]: {
    title: 'Harassment',
    description: 'Bullying, intimidation, or targeted harassment'
  },
  [ReportCategory.HATE_SPEECH]: {
    title: 'Hate Speech',
    description: 'Content promoting hatred based on identity or beliefs'
  },
  [ReportCategory.VIOLENCE]: {
    title: 'Violence or Threats',
    description: 'Threats of violence or promoting dangerous activities'
  },
  [ReportCategory.DOXXING]: {
    title: 'Personal Information Sharing',
    description: 'Sharing private personal information without consent'
  },
  [ReportCategory.FAKE_EVENT]: {
    title: 'Fake Event',
    description: 'Event that doesn\'t exist or is intentionally misleading'
  },
  [ReportCategory.DANGEROUS_EVENT]: {
    title: 'Dangerous Event',
    description: 'Event that poses safety risks to participants'
  },
  [ReportCategory.PRICE_FRAUD]: {
    title: 'Price Fraud',
    description: 'Misleading pricing or payment scams'
  },
  [ReportCategory.LOCATION_FRAUD]: {
    title: 'Location Fraud',
    description: 'Incorrect or misleading event location information'
  },
  [ReportCategory.FAKE_PROFILE]: {
    title: 'Fake Profile',
    description: 'Profile using false identity or information'
  },
  [ReportCategory.IMPERSONATION]: {
    title: 'Impersonation',
    description: 'Pretending to be someone else'
  },
  [ReportCategory.SCAMMER]: {
    title: 'Scammer',
    description: 'User engaged in fraudulent activities'
  },
  [ReportCategory.OTHER]: {
    title: 'Other',
    description: 'Issue not covered by other categories'
  }
};

// Priority mapping based on category
export const CATEGORY_PRIORITY_MAP: Record<ReportCategory, ReportPriority> = {
  [ReportCategory.VIOLENCE]: 'urgent',
  [ReportCategory.DOXXING]: 'urgent',
  [ReportCategory.DANGEROUS_EVENT]: 'urgent',
  [ReportCategory.HARASSMENT]: 'high',
  [ReportCategory.HATE_SPEECH]: 'high',
  [ReportCategory.SCAMMER]: 'high',
  [ReportCategory.FAKE_EVENT]: 'medium',
  [ReportCategory.PRICE_FRAUD]: 'medium',
  [ReportCategory.LOCATION_FRAUD]: 'medium',
  [ReportCategory.IMPERSONATION]: 'medium',
  [ReportCategory.INAPPROPRIATE_CONTENT]: 'medium',
  [ReportCategory.MISLEADING_INFO]: 'low',
  [ReportCategory.COPYRIGHT]: 'low',
  [ReportCategory.FAKE_PROFILE]: 'low',
  [ReportCategory.SPAM]: 'low',
  [ReportCategory.OTHER]: 'low'
};